/**
 * Morning Briefing Overlay — erscheint nach jedem tick_complete Event.
 * Issue #52
 */
import { fixture } from '../data/fixture';
import { t } from '../i18n';
import { formatCash } from '../main';
import { balanceCategoryLabel } from '../utils/balance-label';

export interface BriefingPayload {
  sessionId: string;
  day: number;
  gameOver: boolean;
  winner?: string;
}

/**
 * renderBriefing creates the overlay element.
 * @param day       The day that was just resolved.
 * @param onClose   Called when the overlay should be dismissed.
 * @param onPlan    Called when the user clicks "Zur Planung".
 * @param onPlayback Called when the user clicks "Playback".
 */
export function renderBriefing(
  day: number,
  onClose: () => void,
  onPlan: () => void,
  onPlayback: () => void,
): HTMLElement {
  const p           = fixture.player;
  const notifications = fixture.notifications ?? [];
  const gazette       = fixture.gazette ?? [];
  const runners       = p.runners ?? [];
  const balance       = p.dailyBalance;

  // ── Compute summary values ─────────────────────────────────────────────
  const totalIncome   = balance?.income.reduce((s, l) => s + l.amount, 0) ?? 0;
  const totalExpenses = balance?.expenses.reduce((s, l) => s + l.amount, 0) ?? 0;
  const netChange     = totalIncome - totalExpenses;
  const netColor      = netChange >= 0 ? 'var(--green)' : 'var(--red)';
  const netSign       = netChange >= 0 ? '+' : '';

  const critCount  = notifications.filter(n => n.level === 'crit').length;
  const warnCount  = notifications.filter(n => n.level === 'warn').length;

  const activeRunners   = runners.filter(r => r.status === 'active').length;
  const arrestedRunners = runners.filter(r => r.status === 'arrested').length;
  const injuredRunners  = runners.filter(r => r.status === 'injured').length;

  const topGazette = gazette.slice(0, 2);

  // ── Build income/expense breakdown lines ───────────────────────────────
  const incomeLines = (balance?.income ?? []).map(l =>
    `<div style="display:flex;justify-content:space-between;font-size:12px;padding:2px 0">
      <span style="color:var(--text-lo)">${balanceCategoryLabel(l)}</span>
      <span style="color:var(--green);font-family:var(--mono)">${formatCash(l.amount)}</span>
    </div>`
  ).join('');

  const expenseLines = (balance?.expenses ?? []).map(l =>
    `<div style="display:flex;justify-content:space-between;font-size:12px;padding:2px 0">
      <span style="color:var(--text-lo)">${balanceCategoryLabel(l)}</span>
      <span style="color:var(--red);font-family:var(--mono)">-${formatCash(l.amount)}</span>
    </div>`
  ).join('');

  const gazetteCards = topGazette.map(g =>
    `<div style="background:rgba(255,255,255,0.03);border:1px solid var(--border);border-radius:4px;padding:10px;border-left:3px solid var(--amber)">
      <div style="font-size:12px;font-family:var(--mono);color:var(--text-lo);text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">${g.category} · ${g.type}</div>
      <div style="font-size:13px;font-weight:600;color:var(--text-hi);margin-bottom:4px">${g.headline}</div>
      <div style="font-size:11px;color:var(--text-lo)">${g.lead}</div>
    </div>`
  ).join('');

  const notifBadges = [];
  if (critCount > 0) notifBadges.push(`<span class="chip danger">${critCount} ${t('briefing.notif_crit')}</span>`);
  if (warnCount > 0) notifBadges.push(`<span class="chip warn">${warnCount} ${t('briefing.notif_warn')}</span>`);
  if (notifBadges.length === 0) notifBadges.push(`<span style="color:var(--text-lo);font-size:11px;font-family:var(--mono)">${t('briefing.no_notifications')}</span>`);

  // ── Overlay element ─────────────────────────────────────────────────────
  const overlay = document.createElement('div');
  overlay.id = 'briefing-overlay';
  overlay.className = 'overlay';
  overlay.style.animation = 'briefingFadeIn 0.2s ease';

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.style.cssText = 'width:min(680px,95vw);max-height:90vh;overflow-y:auto;padding:0';

  modal.innerHTML = `
    <style>
      @keyframes briefingFadeIn { from { opacity:0; transform:scale(0.96) } to { opacity:1; transform:scale(1) } }
    </style>

    <!-- Header -->
    <div style="padding:20px 24px 16px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between">
      <div>
        <div style="font-family:var(--mono);font-size:12px;letter-spacing:2px;text-transform:uppercase;color:var(--text-lo);margin-bottom:4px">
          ${t('briefing.cycle_complete', { day })}
        </div>
        <div style="font-size:22px;font-weight:700;color:var(--text-hi)">
          ${t('briefing.title')} <span style="color:var(--cyan)">·</span> ${p.corpName.toUpperCase()}
        </div>
      </div>
      <button id="briefing-close" class="btn-icon" style="width:32px;height:32px;font-size:16px">✕</button>
    </div>

    <!-- Net Worth Banner -->
    <div style="padding:12px 24px;background:rgba(var(--ac),0.04);border-bottom:1px solid var(--border);display:flex;gap:24px">
      <div>
        <div style="font-size:12px;color:var(--text-lo);font-family:var(--mono);text-transform:uppercase;letter-spacing:1px">Net Worth</div>
        <div style="font-size:18px;font-weight:700;color:var(--amber);font-family:var(--mono)">${formatCash(p.netWorth)}</div>
      </div>
      <div>
        <div style="font-size:12px;color:var(--text-lo);font-family:var(--mono);text-transform:uppercase;letter-spacing:1px">${t('briefing.daily_balance')}</div>
        <div style="font-size:18px;font-weight:700;color:${netColor};font-family:var(--mono)">${netSign}${formatCash(netChange)}</div>
      </div>
      <div>
        <div style="font-size:12px;color:var(--text-lo);font-family:var(--mono);text-transform:uppercase;letter-spacing:1px">Federal Heat</div>
        <div style="font-size:18px;font-weight:700;color:${p.federalHeat >= 60 ? 'var(--red)' : p.federalHeat >= 40 ? 'var(--amber)' : 'var(--green)'};font-family:var(--mono)">${Math.round(p.federalHeat)}%</div>
      </div>
    </div>

    <!-- Main grid -->
    <div style="padding:20px 24px;display:grid;grid-template-columns:1fr 1fr;gap:16px">

      <!-- Finanzen -->
      <div>
        <div style="font-family:var(--mono);font-size:12px;letter-spacing:1px;text-transform:uppercase;color:var(--text-lo);margin-bottom:8px">${t('briefing.income')}</div>
        ${incomeLines || `<div style="font-size:12px;color:var(--text-lo)">${t('briefing.no_data')}</div>`}
        <div style="border-top:1px solid var(--border);margin-top:6px;padding-top:6px;display:flex;justify-content:space-between;font-size:12px">
          <span style="color:var(--text-lo)">${t('briefing.total')}</span>
          <span style="color:var(--green);font-family:var(--mono);font-weight:700">${formatCash(totalIncome)}</span>
        </div>
      </div>

      <div>
        <div style="font-family:var(--mono);font-size:12px;letter-spacing:1px;text-transform:uppercase;color:var(--text-lo);margin-bottom:8px">${t('briefing.expenses')}</div>
        ${expenseLines || `<div style="font-size:12px;color:var(--text-lo)">${t('briefing.no_data')}</div>`}
        <div style="border-top:1px solid var(--border);margin-top:6px;padding-top:6px;display:flex;justify-content:space-between;font-size:12px">
          <span style="color:var(--text-lo)">${t('briefing.total')}</span>
          <span style="color:var(--red);font-family:var(--mono);font-weight:700">-${formatCash(totalExpenses)}</span>
        </div>
      </div>

      <!-- Runner Status -->
      <div>
        <div style="font-family:var(--mono);font-size:12px;letter-spacing:1px;text-transform:uppercase;color:var(--text-lo);margin-bottom:8px">${t('briefing.runner_status')}</div>
        <div style="display:flex;gap:12px;flex-wrap:wrap">
          <div style="text-align:center">
            <div style="font-size:22px;font-weight:700;color:var(--green);font-family:var(--mono)">${activeRunners}</div>
            <div style="font-size:12px;color:var(--text-lo)">${t('briefing.active')}</div>
          </div>
          <div style="text-align:center">
            <div style="font-size:22px;font-weight:700;color:var(--amber);font-family:var(--mono)">${injuredRunners}</div>
            <div style="font-size:12px;color:var(--text-lo)">${t('briefing.injured')}</div>
          </div>
          <div style="text-align:center">
            <div style="font-size:22px;font-weight:700;color:var(--red);font-family:var(--mono)">${arrestedRunners}</div>
            <div style="font-size:12px;color:var(--text-lo)">${t('briefing.arrested')}</div>
          </div>
        </div>
      </div>

      <!-- Meldungen -->
      <div>
        <div style="font-family:var(--mono);font-size:12px;letter-spacing:1px;text-transform:uppercase;color:var(--text-lo);margin-bottom:8px">${t('briefing.notifications')}</div>
        <div style="display:flex;gap:8px;flex-wrap:wrap">${notifBadges.join('')}</div>
      </div>

    </div>

    ${topGazette.length > 0 ? `
    <!-- Gazette -->
    <div style="padding:0 24px 20px">
      <div style="font-family:var(--mono);font-size:12px;letter-spacing:1px;text-transform:uppercase;color:var(--text-lo);margin-bottom:10px">${t('briefing.gazette')}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px">${gazetteCards}</div>
    </div>
    ` : ''}

    <!-- CTA Buttons -->
    <div style="padding:16px 24px;border-top:1px solid var(--border);display:flex;gap:10px;justify-content:flex-end">
      <button id="briefing-playback" class="btn sm violet">
        ${t('briefing.btn_playback')}
      </button>
      <button id="briefing-plan" class="btn cyan">
        ${t('briefing.btn_plan')} &#9658;
      </button>
    </div>
  `;

  overlay.appendChild(modal);

  // Wire buttons
  modal.querySelector('#briefing-close')?.addEventListener('click', () => {
    overlay.remove();
    onClose();
  });
  modal.querySelector('#briefing-plan')?.addEventListener('click', () => {
    overlay.remove();
    onPlan();
  });
  modal.querySelector('#briefing-playback')?.addEventListener('click', () => {
    overlay.remove();
    onPlayback();
  });

  // Click outside to close
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      overlay.remove();
      onClose();
    }
  });

  return overlay;
}

// ── Game Over Overlay ────────────────────────────────────────────────────────

function renderGameOver(winner: string, onClose: () => void): HTMLElement {
  const overlay = document.createElement('div');
  overlay.id = 'briefing-overlay';
  overlay.className = 'overlay';
  overlay.style.animation = 'briefingFadeIn 0.3s ease';

  overlay.innerHTML = `
    <style>@keyframes briefingFadeIn { from{opacity:0;transform:scale(0.94)} to{opacity:1;transform:scale(1)} }</style>
    <div class="modal" style="width:min(480px,92vw);padding:40px 36px;text-align:center;border-color:rgba(252,163,17,0.5);box-shadow:0 0 64px rgba(252,163,17,0.18),0 24px 64px rgba(0,0,0,0.6)">
      <div style="font-family:var(--mono);font-size:12px;letter-spacing:3px;color:var(--amber);text-transform:uppercase;margin-bottom:14px">
        ${t('briefing.season_ended')}
      </div>
      <div style="font-size:2.4rem;font-weight:800;color:var(--text-hi);letter-spacing:0.08em;margin-bottom:6px">
        GAME OVER
      </div>
      <div style="font-size:1rem;color:var(--amber);font-family:var(--mono);margin-bottom:32px">
        ${t('briefing.winner')} <span style="color:var(--text-hi);font-weight:700">${winner}</span>
      </div>
      <div style="display:flex;gap:12px;justify-content:center">
        <button id="go-leaderboard" class="btn amber">
          ${t('briefing.btn_leaderboard')}
        </button>
        <button id="go-lobby" class="btn">
          ${t('briefing.btn_lobby')}
        </button>
      </div>
    </div>
  `;

  overlay.querySelector('#go-leaderboard')?.addEventListener('click', () => {
    overlay.remove();
    onClose();
  });
  overlay.querySelector('#go-lobby')?.addEventListener('click', () => {
    overlay.remove();
    window.location.reload();
  });

  // Escape to close
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { overlay.remove(); onClose(); document.removeEventListener('keydown', onKey); }
  };
  document.addEventListener('keydown', onKey);

  return overlay;
}

/**
 * showBriefing mounts the overlay onto the document body and wires navigation.
 */
export function showBriefing(
  payload: BriefingPayload,
  navigateTo: (screen: string) => void,
): void {
  // Remove any existing briefing
  document.getElementById('briefing-overlay')?.remove();

  // Game over: show dedicated winner screen instead
  if (payload.gameOver) {
    const winner = payload.winner ?? '???';
    const overlay = renderGameOver(winner, () => navigateTo('leaderboard'));
    document.body.appendChild(overlay);
    return;
  }

  // Once-per-tick guard: skip if already seen this day
  const seenKey = `briefing:seen:${payload.day}`;
  try {
    if (localStorage.getItem(seenKey) === '1') return;
    localStorage.setItem(seenKey, '1');
  } catch { /* ignore */ }

  const overlay = renderBriefing(
    payload.day,
    () => {}, // close → do nothing extra
    () => navigateTo('plan'),
    () => navigateTo('playback'),
  );

  // Escape / Space to close
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === ' ') {
      e.preventDefault();
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    }
  };
  document.addEventListener('keydown', onKey);
  overlay.addEventListener('remove', () => document.removeEventListener('keydown', onKey));

  document.body.appendChild(overlay);
}
